import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  deleteCapacidad,
  getCapacidadById,
  getPacienteInformeById,
  updateCapacidad,
} from '../api/capacidadFuncionalApi';

const EMPTY_FIELDS = {
  tiempoPiesJuntos: '',
  tiempoTandem: '',
  tiempoSemitandem: '',
  tiempoDesplazamiento: '',
  velocidadMarcha: '',
  sentadillas: '',
  timeUpAndGo: '',
  puntosSPPB: '',
  falloEjecucion: false,
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

export const useCapacidadFuncionalViewer = ({ idTipo, paciente }) => {
  const navigate = useNavigate();
  const [patientInfo, setPatientInfo] = useState({
    codigo: '',
    createdAt: '',
    nombre: '',
    apellidos: '',
    codigoInforme: '',
  });
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [isEditing, setIsEditing] = useState(false);
  const [openGood, setOpenGood] = useState(false);
  const [originalValues, setOriginalValues] = useState(EMPTY_FIELDS);

  useEffect(() => {
    if (!idTipo || !paciente) return;
    Promise.all([getPacienteInformeById(paciente), getCapacidadById(idTipo)])
      .then(([pacienteData, data]) => {
        setPatientInfo({
          codigo: pacienteData.codHistorico || String(pacienteData.id || ''),
          createdAt: formatDate(data.createdAt),
          nombre: pacienteData.firstname || '',
          apellidos: pacienteData.lastname || '',
          codigoInforme: data.codigo || '',
        });
        setFields({
          tiempoPiesJuntos: String(data.tiempoPiesJuntos ?? ''),
          tiempoTandem: String(data.tiempoTandem ?? ''),
          tiempoSemitandem: String(data.tiempoSemitandem ?? ''),
          tiempoDesplazamiento: String(data.tiempoDesplazamiento ?? ''),
          velocidadMarcha: String(data.velocidadMarcha ?? ''),
          sentadillas: String(data.sentadillas ?? ''),
          timeUpAndGo: String(data.timeUpAndGo ?? ''),
          puntosSPPB: String(data.puntosSPPB ?? ''),
          falloEjecucion: data.falloEjecucion ?? false,
        });
      })
      .catch(console.error);
  }, [idTipo, paciente]);

  const setField = (name, value) => setFields((prev) => ({ ...prev, [name]: value }));

  const handleEdit = () => {
    setOriginalValues(fields);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFields(originalValues);
    setIsEditing(false);
  };

  const handleSave = () => {
    updateCapacidad({
      codigoInforme: patientInfo.codigoInforme,
      payload: {
        tiempoPiesJuntos: Number(fields.tiempoPiesJuntos),
        tiempoTandem: Number(fields.tiempoTandem),
        tiempoSemitandem: Number(fields.tiempoSemitandem),
        tiempoDesplazamiento: Number(fields.tiempoDesplazamiento),
        velocidadMarcha: parseFloat(fields.velocidadMarcha),
        sentadillas: Number(fields.sentadillas),
        timeUpAndGo: Number(fields.timeUpAndGo),
        puntosSPPB: Number(fields.puntosSPPB),
        falloEjecucion: fields.falloEjecucion,
      },
    })
      .then(() => {
        setOpenGood(true);
        setIsEditing(false);
      })
      .catch(console.error);
  };

  const handleDelete = () => {
    if (!window.confirm('¿Está seguro de que desea eliminar este informe?')) return;
    deleteCapacidad(patientInfo.codigoInforme)
      .then(() => { navigate('/home'); })
      .catch(console.error);
  };

  const handleExportPDF = () => {
    setIsEditing(false);
    const input = document.getElementById('pdf-content');
    html2canvas(input, {
      scale: 2,
      backgroundColor: null,
      useCORS: true,
      ignoreElements: (el) => el.tagName === 'BUTTON',
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`capacidad_funcional_${patientInfo.nombre}${patientInfo.apellidos}_${patientInfo.createdAt}.pdf`);
      })
      .catch(console.error);
  };

  const metrics = useMemo(() => {
    const puntosSPPB = parseFloat(fields.puntosSPPB);
    const velocidadMarcha = parseFloat(fields.velocidadMarcha);
    const timeUpAndGo = parseFloat(fields.timeUpAndGo);
    return [
      { label: 'Puntos SPPB', value: Number.isNaN(puntosSPPB) ? 'N/D' : puntosSPPB.toFixed(0), unit: 'puntos' },
      { label: 'Velocidad marcha', value: Number.isNaN(velocidadMarcha) ? 'N/D' : velocidadMarcha.toFixed(2), unit: 'm/s' },
      { label: 'TUG', value: Number.isNaN(timeUpAndGo) ? 'N/D' : timeUpAndGo.toFixed(2), unit: 's' },
    ];
  }, [fields.puntosSPPB, fields.velocidadMarcha, fields.timeUpAndGo]);

  return {
    patientInfo,
    fields,
    isEditing,
    openGood,
    metrics,
    setOpenGood,
    setField,
    handleEdit,
    handleCancel,
    handleSave,
    handleDelete,
    handleExportPDF,
  };
};
